import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { StatusPill } from '@ui-kit';
import type { InvokeResultDto, ApiError } from '../types/integrationHub';

interface InvokeResultDrawerProps {
  result: InvokeResultDto | null;
  error: ApiError | null;
  isPending: boolean;
  onClose: () => void;
  onCopyTrace: (traceId: string) => void;
}

/**
 * InvokeResultDrawer — 연동 호출 결과 표시 우측 패널 (dialog 상태)
 */
export function InvokeResultDrawer({
  result,
  error,
  isPending,
  onClose,
  onCopyTrace,
}: InvokeResultDrawerProps) {
  const { t } = useTranslation();
  const closeButtonRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleKeyDown);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [onClose]);

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        zIndex: 900,
        display: 'flex',
        justifyContent: 'flex-end',
      }}
    >
      {/* 오버레이 */}
      <div
        style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.35)' }}
        onClick={onClose}
        aria-hidden="true"
      />
      {/* 패널 */}
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="invoke-result-title"
        style={{
          position: 'relative',
          width: 480,
          maxWidth: '95vw',
          height: '100%',
          overflowY: 'auto',
          background: 'var(--color-surface-background)',
          boxShadow: 'var(--shadow-xl)',
          padding: 'var(--spacing-6)',
          display: 'flex',
          flexDirection: 'column',
          gap: 'var(--spacing-4)',
        }}
      >
        {/* 헤더 */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2
            id="invoke-result-title"
            style={{ fontSize: 'var(--font-size-lg)', fontWeight: 700, color: 'var(--color-text-primary)' }}
          >
            {t('integrationHub.invoke.result.title')}
          </h2>
          <button
            ref={closeButtonRef}
            type="button"
            aria-label={t('integrationHub.invoke.button.close')}
            onClick={onClose}
            style={{
              background: 'transparent',
              border: 'none',
              fontSize: 'var(--font-size-lg)',
              cursor: 'pointer',
              color: 'var(--color-text-secondary)',
              lineHeight: 1,
              padding: 'var(--spacing-1)',
            }}
          >
            ✕
          </button>
        </div>

        {/* loading 상태 */}
        {isPending && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--spacing-3)' }}>
            {Array.from({ length: 4 }).map((_, i) => (
              <div
                key={i}
                style={{
                  height: 20,
                  width: `${60 + (i * 12) % 30}%`,
                  borderRadius: 'var(--radius-sm)',
                  background: 'var(--color-surface-elevated)',
                  animation: 'pulse 1.5s ease-in-out infinite',
                }}
              />
            ))}
          </div>
        )}

        {/* error 상태 */}
        {!isPending && error && (
          <div
            role="alert"
            aria-describedby="invoke-error-desc"
            style={{
              padding: 'var(--spacing-4)',
              border: '1px solid var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-danger-subtle, var(--color-surface-muted))',
              color: 'var(--color-danger)',
            }}
          >
            <strong>{t(`integrationHub.error.${error.code}`, { defaultValue: error.message })}</strong>
            {error.traceId && (
              <p
                id="invoke-error-desc"
                style={{ fontSize: 'var(--font-size-xs)', marginTop: 'var(--spacing-2)', fontFamily: 'var(--font-family-mono)', color: 'var(--color-text-secondary)' }}
              >
                Trace ID: {error.traceId}
              </p>
            )}
          </div>
        )}

        {/* default 상태 — 결과 표시 */}
        {!isPending && result && (
          <>
            {/* 상태 요약 */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr',
                gap: 'var(--spacing-3)',
              }}
            >
              <div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  {t('integrationHub.invoke.result.status')}
                </p>
                <span style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>
                  {result.responseStatus ?? '-'}
                </span>
              </div>
              <div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  {t('integrationHub.invoke.result.duration')}
                </p>
                <span style={{ fontFamily: 'var(--font-family-mono)', fontWeight: 700, fontSize: 'var(--font-size-sm)' }}>
                  {result.durationMs != null ? `${result.durationMs}ms` : '-'}
                </span>
              </div>
              <div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  결과
                </p>
                <StatusPill tone={result.result === 'SUCCESS' ? 'success' : 'danger'}>
                  {t(`integrationHub.result.${result.result}`)}
                </StatusPill>
              </div>
              <div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  {t('integrationHub.invoke.result.demoMode')}
                </p>
                <StatusPill tone={result.demoMode ? 'warning' : 'neutral'}>
                  {result.demoMode ? 'ON' : 'OFF'}
                </StatusPill>
              </div>
            </div>

            {/* Trace ID */}
            <div>
              <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                {t('integrationHub.invoke.result.traceId')}
              </p>
              <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--spacing-2)' }}>
                <code style={{ fontFamily: 'var(--font-family-mono)', fontSize: 'var(--font-size-xs)', color: 'var(--color-text-primary)', wordBreak: 'break-all' }}>
                  {result.traceId}
                </code>
                {result.traceId && (
                  <button
                    type="button"
                    aria-label={t('integrationHub.invoke.button.copyTrace')}
                    onClick={() => onCopyTrace(result.traceId)}
                    style={{
                      background: 'transparent',
                      border: '1px solid var(--color-surface-border)',
                      borderRadius: 'var(--radius-sm)',
                      padding: 'var(--spacing-1)',
                      cursor: 'pointer',
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-secondary)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    복사
                  </button>
                )}
              </div>
            </div>

            {/* 응답 미리보기 */}
            {result.responsePreview != null && (
              <div>
                <p style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-secondary)', marginBottom: 'var(--spacing-1)' }}>
                  {t('integrationHub.invoke.result.preview')}
                </p>
                <pre
                  style={{
                    background: 'var(--color-surface-muted)',
                    border: '1px solid var(--color-surface-border)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--spacing-3)',
                    fontSize: 'var(--font-size-xs)',
                    fontFamily: 'var(--font-family-mono)',
                    overflowX: 'auto',
                    color: 'var(--color-text-primary)',
                    maxHeight: 300,
                    overflowY: 'auto',
                    whiteSpace: 'pre-wrap',
                    wordBreak: 'break-all',
                  }}
                >
                  {JSON.stringify(result.responsePreview, null, 2)}
                </pre>
              </div>
            )}
          </>
        )}

        {/* 닫기 버튼 */}
        <div style={{ marginTop: 'auto', paddingTop: 'var(--spacing-4)' }}>
          <button
            type="button"
            onClick={onClose}
            style={{
              width: '100%',
              padding: 'var(--spacing-2) var(--spacing-4)',
              background: 'var(--color-surface-elevated)',
              border: '1px solid var(--color-surface-border)',
              borderRadius: 'var(--radius-md)',
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
              fontFamily: 'var(--font-family-sans)',
            }}
          >
            {t('integrationHub.invoke.button.close')}
          </button>
        </div>
      </div>

      <style>{`
        @keyframes pulse {
          0%, 100% { opacity: 1; }
          50% { opacity: 0.4; }
        }
      `}</style>
    </div>
  );
}
